import type {CreateBusinessRFIPayload} from '@/types';

/**
 * Validates the Create RFI payload [D4]: trims requested-document names, requires at
 * least one, rejects blanks and case-insensitive duplicates.
 */
export class BusinessRFIValidator {
  constructor(private request: Partial<CreateBusinessRFIPayload>) {}

  private throwErrorMessage(errorMessage: string): never {
    throw new Error(errorMessage);
  }

  /**
   * The document-list half of the rules, on its own, because naming the ask on
   * a processor-raised draft needs exactly these checks and none of the others
   * (a draft already has its businessId and description). Kept here rather than
   * duplicated in the hook so the draft path cannot drift from the create path:
   * it previously skipped the duplicate check and let the server reject the
   * request instead, which surfaced as a bare status code.
   */
  static normalizeRequestedDocuments(
    requestedDocuments: (string | undefined)[] = [],
  ): string[] {
    const trimmed = requestedDocuments
      .map((doc) => doc?.trim())
      .filter((doc): doc is string => !!doc);

    if (trimmed.length === 0) {
      throw new Error('Add at least one requested document');
    }

    const seen = new Set<string>();
    for (const doc of trimmed) {
      const key = doc.toLowerCase();
      if (seen.has(key)) {
        throw new Error(`Duplicate requested document: ${doc}`);
      }
      seen.add(key);
    }
    return trimmed;
  }

  value(): CreateBusinessRFIPayload {
    const {businessId} = this.request;
    if (!businessId) {
      this.throwErrorMessage('No business selected');
    }

    const trimmedDocuments = BusinessRFIValidator.normalizeRequestedDocuments(
      this.request.requestedDocuments,
    );

    const description = this.request.description?.trim();
    if (!description) {
      this.throwErrorMessage('Add a description');
    }

    return {
      businessId: businessId,
      requestedDocuments: trimmedDocuments,
      customerEmail: this.request.customerEmail?.trim() || undefined,
      description,
      meta: this.request.meta,
      transactionId: this.request.transactionId?.trim() || undefined,
    };
  }
}
